import { useEffect, useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";

/**
 * Prova ITLY — playground with realistic demos.
 * Crowd Meter: season toggle + realistic per-season curves for Portonovo (Conero beach)
 * Points demo: category + gem flag → qualitative reward
 */

const CROWD_CURVES = {
    alta: {
        labelKey: "try.season.alta.label",
        noteKey: "try.season.alta.note",
        data: [
            { h: 6, level: 0.02 }, { h: 7, level: 0.05 }, { h: 8, level: 0.15 },
            { h: 9, level: 0.35 }, { h: 10, level: 0.60 }, { h: 11, level: 0.82 },
            { h: 12, level: 0.95 }, { h: 13, level: 0.98 }, { h: 14, level: 0.95 },
            { h: 15, level: 0.88 }, { h: 16, level: 0.78 }, { h: 17, level: 0.62 },
            { h: 18, level: 0.42 }, { h: 19, level: 0.22 }, { h: 20, level: 0.10 },
            { h: 21, level: 0.05 }, { h: 22, level: 0.03 },
        ],
    },
    media: {
        labelKey: "try.season.media.label",
        noteKey: "try.season.media.note",
        data: [
            { h: 6, level: 0.01 }, { h: 7, level: 0.03 }, { h: 8, level: 0.08 },
            { h: 9, level: 0.18 }, { h: 10, level: 0.32 }, { h: 11, level: 0.48 },
            { h: 12, level: 0.58 }, { h: 13, level: 0.62 }, { h: 14, level: 0.55 },
            { h: 15, level: 0.48 }, { h: 16, level: 0.38 }, { h: 17, level: 0.28 },
            { h: 18, level: 0.18 }, { h: 19, level: 0.10 }, { h: 20, level: 0.05 },
            { h: 21, level: 0.03 }, { h: 22, level: 0.02 },
        ],
    },
    bassa: {
        labelKey: "try.season.bassa.label",
        noteKey: "try.season.bassa.note",
        data: [
            { h: 6, level: 0.01 }, { h: 7, level: 0.02 }, { h: 8, level: 0.03 },
            { h: 9, level: 0.05 }, { h: 10, level: 0.08 }, { h: 11, level: 0.12 },
            { h: 12, level: 0.15 }, { h: 13, level: 0.18 }, { h: 14, level: 0.16 },
            { h: 15, level: 0.14 }, { h: 16, level: 0.11 }, { h: 17, level: 0.07 },
            { h: 18, level: 0.04 }, { h: 19, level: 0.02 }, { h: 20, level: 0.01 },
            { h: 21, level: 0.01 }, { h: 22, level: 0.01 },
        ],
    },
};

const TONES = {
    green: { bg: "var(--itly-surface-green)", bar: "var(--itly-green-ink)", dot: "#25A26A" },
    amber: { bg: "var(--itly-surface-amber)", bar: "#D18A1A", dot: "#E39A20" },
    red: { bg: "var(--itly-surface-peach)", bar: "#C65A38", dot: "#E8714C" },
};

function crowdInfo(level) {
    if (level > 0.85) return { key: "try.crowd.pienissimo", tone: "red" };
    if (level > 0.65) return { key: "try.crowd.molto", tone: "red" };
    if (level > 0.45) return { key: "try.crowd.moderato", tone: "amber" };
    if (level > 0.25) return { key: "try.crowd.tranquillo", tone: "green" };
    if (level > 0.10) return { key: "try.crowd.poca", tone: "green" };
    return { key: "try.crowd.silenzio", tone: "green" };
}

function interpolate(curve, hour) {
    for (let i = 0; i < curve.length - 1; i++) {
        if (hour >= curve[i].h && hour <= curve[i + 1].h) {
            const t = (hour - curve[i].h) / (curve[i + 1].h - curve[i].h);
            return curve[i].level + (curve[i + 1].level - curve[i].level) * t;
        }
    }
    return curve[0].level;
}

// Points demo: qualitative only (gem → boosted points)
const CATEGORIES = [
    { id: "trattoria", labelKey: "try.points.cat.trattoria" },
    { id: "museo", labelKey: "try.points.cat.museo" },
    { id: "esperienza", labelKey: "try.points.cat.esperienza" },
    { id: "guida", labelKey: "try.points.cat.guida" },
];

const SEASON_BUTTONS = [
    { id: "alta", labelKey: "try.season.alta" },
    { id: "media", labelKey: "try.season.media" },
    { id: "bassa", labelKey: "try.season.bassa" },
];

export default function TryItly() {
    const { t } = useI18n();
    const [season, setSeason] = useState("alta");
    const [hour, setHour] = useState(13);
    const curve = CROWD_CURVES[season];
    const level = interpolate(curve.data, hour);
    const info = crowdInfo(level);
    const tone = TONES[info.tone];

    const [cat, setCat] = useState("esperienza");
    const [isGemma, setIsGemma] = useState(true);

    return (
        <section
            id="prova"
            data-testid="try-itly"
            style={{
                background: "var(--itly-page)",
                padding: "104px 32px",
                position: "relative",
                overflow: "hidden",
            }}
            data-reveal="up"
        >
            <div style={{ maxWidth: 1200, margin: "0 auto" }}>
                <div style={{ maxWidth: 780, marginBottom: 48 }}>
                    <p style={eyebrow}>{t("try.eyebrow")}</p>
                    <h2
                        className="itly-serif"
                        style={{
                            marginTop: 14,
                            marginBottom: 0,
                            fontSize: "clamp(34px, 3.8vw, 56px)",
                            lineHeight: 1.05,
                            color: "var(--itly-fg-strong)",
                            letterSpacing: "-0.022em",
                        }}
                    >
                        {t("try.title_a")}
                        <em style={{ fontStyle: "italic", color: "var(--itly-orange-500)", fontWeight: 500 }}>
                            {t("try.title_b")}
                        </em>
                    </h2>
                    <p style={{ marginTop: 18, fontFamily: "Inter, sans-serif", fontSize: 16, color: "var(--itly-muted)", lineHeight: 1.6, maxWidth: 640 }}>
                        {t("try.sub")}
                    </p>
                </div>

                <div className="itly-try-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 24 }}>
                    {/* ─── CROWD METER ────────────────────────────── */}
                    <div className="itly-card" data-testid="crowd-meter" style={{ padding: "28px 28px 24px", display: "flex", flexDirection: "column", gap: 20 }}>
                        <div>
                            <p style={demoLabel}>{t("try.crowd.label")}</p>
                            <h3
                                className="itly-serif"
                                style={{
                                    margin: "8px 0 0",
                                    fontSize: 24,
                                    color: "var(--itly-fg-strong)",
                                    lineHeight: 1.15,
                                    letterSpacing: "-0.01em",
                                }}
                            >
                                {t("try.crowd.place")}
                            </h3>
                            <p style={{ margin: "6px 0 0", fontFamily: "Inter, sans-serif", fontSize: 13, color: "var(--itly-muted)" }}>
                                {t(curve.labelKey)}
                            </p>
                        </div>

                        <div data-testid="season-picker" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {SEASON_BUTTONS.map((s) => (
                                <button
                                    key={s.id}
                                    type="button"
                                    onClick={() => setSeason(s.id)}
                                    className={`itly-chip-light ${season === s.id ? "on" : ""}`}
                                    data-testid={`season-${s.id}`}
                                    style={{ padding: "6px 12px", fontSize: 12.5 }}
                                >
                                    {t(s.labelKey)}{t("try.season.suffix")}
                                </button>
                            ))}
                        </div>

                        <div style={{ background: tone.bg, borderRadius: 14, padding: "18px 20px", transition: "background 400ms ease" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                                <span
                                    style={{
                                        fontFamily: "Inter, sans-serif",
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: tone.bar,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 8,
                                    }}
                                >
                                    <span
                                        aria-hidden="true"
                                        style={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: 999,
                                            background: tone.dot,
                                            boxShadow: `0 0 0 4px ${tone.dot}22`,
                                        }}
                                    />
                                    {t(info.key)}
                                </span>
                                <span
                                    style={{
                                        fontFamily: "Fraunces, serif",
                                        fontWeight: 700,
                                        fontSize: 30,
                                        color: "var(--itly-fg-strong)",
                                        letterSpacing: "-0.02em",
                                    }}
                                    data-testid="crowd-hour"
                                >
                                    {String(hour).padStart(2, "0")}:00
                                </span>
                            </div>
                            <div style={{ height: 8, borderRadius: 999, background: "rgba(15, 61, 62, 0.08)", overflow: "hidden" }}>
                                <div
                                    style={{
                                        width: `${level * 100}%`,
                                        height: "100%",
                                        background: tone.bar,
                                        borderRadius: 999,
                                        transition: "width 380ms cubic-bezier(0.2, 0.7, 0.2, 1), background 380ms ease",
                                    }}
                                    data-testid="crowd-bar"
                                />
                            </div>
                        </div>

                        <div>
                            <input
                                type="range"
                                min={6}
                                max={22}
                                step={1}
                                value={hour}
                                onChange={(e) => setHour(Number(e.target.value))}
                                className="itly-range"
                                data-testid="crowd-slider"
                                aria-label={t("try.crowd.hourAria")}
                                style={{ width: "100%" }}
                            />
                            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontFamily: "Inter, sans-serif", fontSize: 11.5, color: "var(--itly-muted)", letterSpacing: "0.06em" }}>
                                <span>06</span><span>10</span><span>14</span><span>18</span><span>22</span>
                            </div>
                        </div>

                        <p style={{ margin: 0, fontFamily: "Inter, sans-serif", fontSize: 13, color: "var(--itly-muted)", lineHeight: 1.55 }}>
                            {t(curve.noteKey)}
                        </p>
                    </div>

                    {/* ─── POINTS CALCULATOR ─────────────────────── */}
                    <div className="itly-card" data-testid="points-calc" style={{ padding: "28px 28px 24px", display: "flex", flexDirection: "column", gap: 18 }}>
                        <div>
                            <p style={demoLabel}>{t("try.points.label")}</p>
                            <h3
                                className="itly-serif"
                                style={{
                                    margin: "8px 0 0",
                                    fontSize: 24,
                                    color: "var(--itly-fg-strong)",
                                    lineHeight: 1.15,
                                    letterSpacing: "-0.01em",
                                }}
                            >
                                {t("try.points.title")}
                            </h3>
                        </div>

                        <div>
                            <span style={fieldLabel}>{t("try.points.categoryField")}</span>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }} data-testid="points-category">
                                {CATEGORIES.map((c) => (
                                    <button
                                        key={c.id}
                                        type="button"
                                        onClick={() => setCat(c.id)}
                                        data-testid={`points-cat-${c.id}`}
                                        className={`itly-chip-light ${cat === c.id ? "on" : ""}`}
                                    >
                                        {t(c.labelKey)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <span style={fieldLabel}>{t("try.points.gemmaField")}</span>
                            <div style={{ display: "flex", gap: 8, marginTop: 8 }} data-testid="points-gemma-toggle">
                                <button
                                    type="button"
                                    onClick={() => setIsGemma(true)}
                                    className={`itly-chip-light ${isGemma ? "on" : ""}`}
                                    data-testid="points-gemma-yes"
                                >
                                    {t("try.points.gemmaYes")}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsGemma(false)}
                                    className={`itly-chip-light ${!isGemma ? "on" : ""}`}
                                    data-testid="points-gemma-no"
                                >
                                    {t("try.points.gemmaNo")}
                                </button>
                            </div>
                        </div>

                        <div
                            style={{
                                background: isGemma ? "var(--itly-peach-050)" : "var(--itly-teal-050)",
                                borderRadius: 14,
                                padding: "20px 22px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 20,
                                transition: "background 400ms ease",
                            }}
                        >
                            <div>
                                <span style={{ display: "block", fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 600, color: isGemma ? "var(--itly-orange-700)" : "var(--itly-fg-strong)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                                    {t("try.points.earn")}
                                </span>
                                <div style={{ marginTop: 4, display: "flex", alignItems: "baseline", gap: 10 }}>
                                    <span
                                        data-testid="points-total"
                                        style={{
                                            fontFamily: "Fraunces, serif",
                                            fontWeight: 700,
                                            fontSize: "clamp(26px, 3vw, 34px)",
                                            lineHeight: 1.1,
                                            color: isGemma ? "var(--itly-orange-700)" : "var(--itly-fg-strong)",
                                            letterSpacing: "-0.02em",
                                            transition: "color 300ms ease",
                                        }}
                                    >
                                        {isGemma ? t("try.points.bonusGem") : t("try.points.bonusBase")}
                                    </span>
                                </div>
                            </div>
                            {isGemma && (
                                <div key={cat} className="itly-x2-badge" data-testid="x2-badge">✦</div>
                            )}
                        </div>

                        <p style={{ margin: 0, fontFamily: "Inter, sans-serif", fontSize: 13, color: "var(--itly-muted)", lineHeight: 1.55 }}>
                            {t("try.points.disclaimer")}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}

const eyebrow = {
    fontFamily: "Inter, sans-serif",
    fontSize: 12.5,
    fontWeight: 700,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: "var(--itly-orange-700)",
    margin: 0,
};
const demoLabel = {
    margin: 0,
    fontFamily: "Inter, sans-serif",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: "var(--itly-orange-700)",
};
const fieldLabel = {
    display: "block",
    fontFamily: "Inter, sans-serif",
    fontSize: 13,
    fontWeight: 600,
    color: "var(--itly-fg-strong)",
    letterSpacing: "0.005em",
};
